/**
 * Guard for `<select>`-sourced identifiers before they are interpolated into
 * a v2 API path (SRA003-454).
 *
 * The block-assignment selects render an unvalued placeholder option
 * (`<option>Select Farm</option>`), so picking it — or never picking anything
 * — yields the literal label `"Select Farm"` (or `undefined`). Fired into a
 * loader that builds `/group/{id}/farms/{farm}/blocks`, that produces
 * `/farms/Select%20Farm/blocks` (500) and `/group/undefined/growers` (404).
 *
 * A real identifier here is a non-empty token with no whitespace. We
 * deliberately do NOT require a strict UUID: some farm selects bind the
 * numeric `Farm.id`, others the UUID `ext_id`, and both are valid targets —
 * the placeholder/whitespace check rules out every observed bad value without
 * rejecting a legitimate id.
 */
export function isUsableId(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed === '') return false;
  if (trimmed === 'undefined' || trimmed === 'null') return false;
  // Whitespace catches every placeholder label ("Select Farm", "Select
  // Season", …); no real id (numeric or UUID) contains a space.
  if (/\s/.test(value)) return false;
  return true;
}
